// Final Review pop-up (M7-F4 / F5; rows M7.10 / M7.20 / M7.26 / M7.34; DC-86 / DC-98 / DC-122 / DC-167), prototype finalReviewModal.
// Eight sections, one open at a time; a section after the first unticked one stays locked. After every section is ticked
// the confirmation appears. Approve is server side (POST /crm2/final-review/:id/approve): it checks every step again,
// the sanction, the AfB confirmation, an open loyalty decision and the KYC, makes the allotment letter and moves the
// booking to Agreement Preparation. Send back = KYC to correct (M3 send back route).
import React, { useEffect, useState } from "react";
import { Box, Modal } from "@mui/material";
import Close from "../../Icons/Close";
import Button from "../../../Subcomponents/Button";
import { crm2FinalReview, crm2FinalReviewApprove, crm2KycSendBack } from "../../../Services/Crm2List";
import { dismissLoader, presentLoader } from "../../../Services/loader";
import { errorTost, successTost } from "../../../Services/Toast";
import { unitLine } from "../ui/cardParts";
import settingsCss from "../Settings/Crm2Settings.module.css";

const inr = (n) => `₹ ${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const day = (ms) => (ms ? new Date(Number(ms)).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const btnSec = { width: "fit-content", color: "#344054", backgroundColor: "#FFFFFF", border: "1px solid #D0D5DD", fontSize: "1.4rem", lineHeight: "2rem" };
const btnPri = { width: "fit-content", color: "#FFFFFF", backgroundColor: "#155EEF", border: "1px solid #155EEF", fontSize: "1.4rem", lineHeight: "2rem" };
const hint = { fontSize: "1.3rem", color: "#667085" };
const kvRow = { display: "grid", gridTemplateColumns: "22rem 1fr", gap: ".4rem 1.6rem", fontSize: "1.4rem", padding: ".4rem 0" };

const SECTIONS = [
  ["unit", "Booking and unit", "System"],
  ["kyc", "Agreement KYC form", "Filled by the customer · you check it here (the only CRM review)"],
  ["draft", "Review draft and OTP", "Customer"],
  ["disc", "Discounts", "System checks · customer decides · no CRM action"],
  ["av", "Agreement value and payment plan", "CRM"],
  ["park", "Parking", "CRM"],
  ["pay", "SDR and payments", "Customer"],
  ["days", "15-day deadline", "System"],
];

function Kv({ k, children }) {
  return (
    <div style={kvRow}>
      <span style={{ color: "#667085" }}>{k}</span>
      <span style={{ color: "#101828" }}>{children}</span>
    </div>
  );
}

function body(key, s) {
  const x = s.sections?.[key] || {};
  switch (key) {
    case "unit":
      return (
        <>
          <Kv k="Customer">{x.customer} {x.leadDisplayId ? `· ${x.leadDisplayId}` : ""}</Kv>
          <Kv k="Project / unit">{x.project} · {x.unit}</Kv>
          <Kv k="Sanction">{x.sanctioned ? "Sanctioned" : <span style={{ color: "#B54708" }}>Waiting for sanction · Final Review locked</span>}</Kv>
          <Kv k="Sales Manager / CRM">{x.salesManager || "—"} / {x.crm || "—"}</Kv>
          <Kv k="Booking amount paid">{x.bookingAmountPaid ? `Yes · ${inr(x.bookingAmount)}` : "No"}</Kv>
        </>
      );
    case "kyc":
      return (
        <>
          <Kv k="Submitted">{x.status === "DONE" ? "Yes" : x.status === "REOPENED" ? <span style={{ color: "#B42318" }}>Sent back to the customer</span> : "Not yet"}</Kv>
          <Kv k="Applicants">{(x.applicants || []).map((a) => `${a.no}. ${a.name}`).join(" · ") || "—"}</Kv>
          <p style={hint}>Open the full form with ⋮ › Preview Agreement Confirmation Form. If something is wrong, use Send back below.</p>
        </>
      );
    case "draft":
      return s.sections?.draft ? (
        <>
          <Kv k="Review draft">Revision {s.sections.draft.revisionNo}{s.sections.draft.placeholder ? " (placeholder until the template is linked)" : ""}</Kv>
          <Kv k="Confirmed by OTP">{s.sections.draft.allConfirmed ? s.sections.draft.confirmedBy : <span style={{ color: "#B54708" }}>Waiting for {(s.sections.draft.pendingNames || []).join(", ")}</span>}</Kv>
        </>
      ) : (
        <Kv k="Review draft">Not made yet</Kv>
      );
    case "disc": {
      const d = x || {};
      return (
        <>
          <span style={{ display: "inline-block", fontSize: "1.2rem", color: "#027A48", background: "#ECFDF3", borderRadius: "1.6rem", padding: ".2rem .8rem", marginBottom: ".8rem" }}>Checked by the system</span>
          {d.error ? (
            <Kv k="Discounts">{d.error}</Kv>
          ) : (
            <>
              <Kv k="Female 1% stamp duty discount">
                {d.female?.applied ? `Applied by the system · stamp duty ${d.female.sdrPercentBefore}% → ${d.female.sdrPercentAfter}% only, agreement value unchanged` : "Not applicable"}
              </Kv>
              <Kv k="Loyalty discount">{d.loyalty ? d.loyalty.line || d.loyalty.status : "Not a loyalty lead"}</Kv>
              <Kv k="Other discounts">None</Kv>
              {d.price && <Kv k="Total unit cost">{inr(d.price.now?.total)}{d.price.actual?.total !== d.price.now?.total ? ` (actual ${inr(d.price.actual?.total)})` : ""}</Kv>}
            </>
          )}
        </>
      );
    }
    case "av":
      return (
        <>
          <Kv k="Payment plan">{x.plan || "—"}</Kv>
          <Kv k="AV change">{x.changed ? "Changed and approved" : "Not changed"}</Kv>
          <Kv k="Agreement value (AV)">{inr(x.unitCost?.bsp)}</Kv>
          <Kv k="GST">{inr(x.unitCost?.gst)}</Kv>
          <Kv k="SDR + registration + legal">{inr(x.unitCost?.sdr)}</Kv>
          <Kv k={<b>Total unit cost</b>}>
            <b>{inr(x.unitCost?.total)}</b>
          </Kv>
        </>
      );
    case "park":
      return (
        <>
          <Kv k="Selected">{x.status === "NA" ? "No parking for this unit" : x.slots ? `${x.slots} slot(s)` : x.status === "DONE" ? "Done" : "Not yet"}</Kv>
          <Kv k="Upgrade cost">{x.upgradeCost ? inr(x.upgradeCost) : "None"}</Kv>
        </>
      );
    case "pay":
      return (
        <>
          <Kv k="Booking amount">{inr(x.bookingAmount)}</Kv>
          <Kv k="SDR">{inr(x.sdrPaid)} of {inr(x.sdrDue)}</Kv>
          <Kv k="OCR paid">{inr(x.ocrPaid)} (GST {inr(x.gstPaid)})</Kv>
          <Kv k="Balance due now">{inr(x.balanceNow)}</Kv>
        </>
      );
    case "days":
      return (
        <>
          <Kv k="Day 0">{day(x.day0)}</Kv>
          <Kv k="Stage 1 complete by customer">{day(x.stage1CompletedAt)}</Kv>
          <Kv k="Days used">{x.daysUsed === null || x.daysUsed === undefined ? "Clock not started" : `${x.daysUsed} of ${x.total}`}</Kv>
        </>
      );
    default:
      return null;
  }
}

export default function FinalReviewModal({ card, canApprove, onClose, onDone }) {
  const apId = card.allotmentPaymentId;
  const [s, setS] = useState(null);
  const [ticks, setTicks] = useState({});
  const [open, setOpen] = useState("unit");
  const [declared, setDeclared] = useState(false);
  const [sendBack, setSendBack] = useState(null); // null | "" (typing)
  const [err, setErr] = useState("");

  useEffect(() => {
    crm2FinalReview(apId)
      .then((d) => setS(d))
      .catch((e) => {
        errorTost(e?.response?.data?.message || "Could not open the Final Review. Please try again.");
        onClose();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apId]);

  if (!s) return null;
  const ticked = SECTIONS.filter(([k]) => ticks[k]).length;
  const firstOpen = SECTIONS.findIndex(([k]) => !ticks[k]);
  const allTicked = ticked === SECTIONS.length;
  const unsanctioned = s.blocks.some((b) => b.code === "UNSANCTIONED");
  const blocked = s.blocks.filter((b) => b.code !== "UNSANCTIONED");
  const canSubmit = canApprove && allTicked && declared && !s.blocks.length;

  const tick = (k, v) => {
    const next = { ...ticks, [k]: v };
    setTicks(next);
    if (v) {
      const n = SECTIONS.findIndex(([x]) => !next[x]);
      setOpen(n >= 0 ? SECTIONS[n][0] : null);
    }
  };

  const approve = async () => {
    presentLoader();
    try {
      const r = await crm2FinalReviewApprove(apId, { checklist: Object.fromEntries(SECTIONS.map(([k]) => [k, true])), declaration: true });
      console.log("[crm2][FinalReview] approved", { allotmentPaymentId: apId, letter: r?.letter?.state });
      successTost(r?.letter?.state === "MADE" ? "Final Review approved: moved to Agreement Preparation" : "Final Review approved: moved to Agreement Preparation. The allotment letter is being made again.");
      onDone();
    } catch (e) {
      console.error("[crm2][FinalReview] approve failed", { allotmentPaymentId: apId, status: e?.response?.status, message: e?.response?.data?.message });
      errorTost(e?.response?.data?.message || "Could not approve the Final Review. Please try again.");
    } finally {
      dismissLoader();
    }
  };

  const doSendBack = async () => {
    if ((sendBack || "").trim().length < 5) {
      setErr("Please enter the rejection reason");
      return;
    }
    presentLoader();
    try {
      await crm2KycSendBack(apId, sendBack.trim());
      successTost("Sent to the customer: KYC to correct");
      onDone();
    } catch (e) {
      errorTost(e?.response?.data?.message || "Could not send it back. Please try again.");
    } finally {
      dismissLoader();
    }
  };

  return (
    <Modal open onClose={onClose} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
      <Box className={settingsCss.modalWrapper} style={{ width: "min(110rem, 94vw)", maxWidth: "94vw" }}>
        <div className={settingsCss.modalHeader}>
          <span>Final Review</span>
          <span className={settingsCss.modalClose} onClick={onClose} tabIndex={0} role="button" aria-label="Close">
            <Close width="1.2rem" height="1.2rem" />
          </span>
        </div>
        <div className={settingsCss.modalContent} style={{ maxHeight: "72vh", overflow: "auto", background: "#F9FAFB" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "1.6rem", alignItems: "flex-start", marginBottom: "1.2rem" }}>
            <div>
              <p style={{ fontSize: "1.5rem", fontWeight: 600, color: "#101828", margin: 0 }}>
                {unitLine(card)} · {card.leadName} · CRM {card.crmName || "—"}
              </p>
              <p style={{ fontSize: "1.3rem", color: "#B54708", margin: ".4rem 0 0" }}>
                This is the last check. After approval the allotment letter is issued and applicants, price, payment plan and parking cannot be changed.
              </p>
            </div>
            <span style={{ fontSize: "1.2rem", color: "#3538CD", background: "#EEF4FF", borderRadius: "1.6rem", padding: ".2rem .8rem", whiteSpace: "nowrap" }}>
              {ticked} of {SECTIONS.length} checked
            </span>
          </div>
          {blocked.length > 0 && (
            <div style={{ background: "#FFFAEB", border: "1px solid #FEDF89", color: "#B54708", borderRadius: ".8rem", padding: "1rem 1.4rem", fontSize: "1.35rem", marginBottom: "1.2rem" }}>
              {blocked.map((b) => (
                <div key={b.code}>{b.message}</div>
              ))}
            </div>
          )}
          {s.letter?.state === "FAILED" && <p style={{ ...hint, color: "#B42318" }}>Allotment letter not made: {s.letter.reason}</p>}
          {SECTIONS.map(([k, title, who], i) => {
            const locked = firstOpen >= 0 && i > firstOpen;
            const isOpen = open === k && !locked;
            return (
              <div key={k} style={{ background: "#FFFFFF", border: "1px solid #EAECF0", borderRadius: ".8rem", marginBottom: ".8rem", opacity: locked ? 0.45 : 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "1.2rem", padding: "1.2rem 1.6rem", cursor: locked ? "default" : "pointer" }} onClick={() => !locked && setOpen(isOpen ? null : k)}>
                  <span style={{ width: "2.6rem", height: "2.6rem", borderRadius: "50%", display: "inline-grid", placeItems: "center", fontSize: "1.3rem", color: ticks[k] ? "#027A48" : "#3538CD", background: ticks[k] ? "#ECFDF3" : "#EEF4FF" }}>{ticks[k] ? "✓" : i + 1}</span>
                  <span style={{ flex: 1 }}>
                    <b style={{ fontSize: "1.45rem", color: "#101828" }}>{title}</b>
                    <br />
                    <span style={hint}>{who}</span>
                  </span>
                  <label style={{ display: "flex", alignItems: "center", gap: ".6rem", fontSize: "1.35rem", color: "#344054" }} onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" disabled={locked} checked={!!ticks[k]} onChange={(e) => tick(k, e.target.checked)} /> Checked
                  </label>
                </div>
                {isOpen && <div style={{ padding: "0 1.6rem 1.4rem 5.4rem" }}>{body(k, s)}</div>}
              </div>
            );
          })}
          {allTicked && (
            <label style={{ display: "flex", gap: ".8rem", alignItems: "flex-start", border: "1px solid #FEC84B", background: "#FFFAEB", borderRadius: ".8rem", padding: "1.2rem 1.4rem", fontSize: "1.4rem", color: "#344054", marginTop: ".8rem" }}>
              <input type="checkbox" checked={declared} onChange={(e) => setDeclared(e.target.checked)} style={{ marginTop: ".3rem" }} /> {s.declaration}
            </label>
          )}
          {sendBack !== null && (
            <div style={{ marginTop: "1.2rem" }}>
              <label style={{ display: "block", fontSize: "1.4rem", fontWeight: 500, color: "#344054", marginBottom: ".6rem" }}>
                Send back · what the customer must correct <sup style={{ color: "red" }}>*</sup>
              </label>
              <textarea
                value={sendBack}
                onChange={(e) => {
                  setSendBack(e.target.value);
                  setErr("");
                }}
                placeholder="Enter a reason..."
                style={{ width: "100%", minHeight: "7rem", fontSize: "1.5rem", padding: "1rem", border: "1px solid #D0D5DD", borderRadius: ".8rem", boxSizing: "border-box" }}
              />
              {err && <p style={{ color: "#D92D20", fontSize: "1.3rem", margin: ".4rem 0 0" }}>{err}</p>}
            </div>
          )}
        </div>
        <div className={settingsCss.modalFooter} style={{ justifyContent: "space-between", alignItems: "center" }}>
          <span style={hint}>{unsanctioned ? "Unit not sanctioned yet: approval opens when FarVision shows it sanctioned." : allTicked && declared ? "Ready to approve." : "Tick every section and the confirmation to approve."}</span>
          <span style={{ display: "flex", gap: "1rem" }}>
            {sendBack === null ? <Button btnName="Send back" style={btnSec} clicked={() => setSendBack("")} /> : <Button btnName="Reject Confirm" style={btnSec} clicked={doSendBack} />}
            <Button btnName={unsanctioned ? "Approve (after sanction)" : "Approve Final Review"} style={{ ...btnPri, opacity: canSubmit ? 1 : 0.5 }} disable={!canSubmit} clicked={approve} />
          </span>
        </div>
      </Box>
    </Modal>
  );
}
